"use client"

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react"
import { ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { Fragment, ReactNode, useCallback } from "react"

interface Props {
    open: boolean
    close: () => void
    children: ReactNode
}

export function ErrorModal({ children, open, close }: Props) {
    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={close}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="relative rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all duration-300 sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
                                <div className="flex items-center gap-x-5">
                                    <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100/50 sm:mx-0 sm:size-10">
                                        <ExclamationCircleIcon className="size-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <DialogTitle
                                        as="h3"
                                        className="inline-block grow align-bottom text-base font-semibold text-gray-900"
                                    >
                                        Error
                                    </DialogTitle>
                                </div>

                                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                    <button
                                        type="button"
                                        className="focus:ring-primary-500 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                                        onClick={close}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="size-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div>
                                    <div className="mt-6 border-t border-gray-100">{children}</div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
